import React, { useState, useEffect, useRef } from 'react';

import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Portal,
} from '@chakra-ui/react';

import { CiClock1 } from 'react-icons/ci';

import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import './DateField.css';

// Inject 12:01 AM and 12:01 PM as custom times
const getInjectedTimes = (): Date[] => {
  const baseDate = new Date();
  baseDate.setSeconds(0);
  baseDate.setMilliseconds(0);

  // 12:01 AM
  const time1201AM = new Date(baseDate);
  time1201AM.setHours(0);
  time1201AM.setMinutes(1);

  // 12:01 PM
  const time1201PM = new Date(baseDate);
  time1201PM.setHours(12);
  time1201PM.setMinutes(1);

  return [time1201AM, time1201PM];
};

// Filter out 12:00 AM and 12:00 PM (replaced by 12:01)
const filterTime = (time: Date): boolean => {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  // Exclude 12:00 AM (0:00) and 12:00 PM (12:00)
  if ((hours === 0 || hours === 12) && minutes === 0) {
    return false;
  }
  return true;
};

const TimeField = ({
  label,
  value,
  onChange,
  required,
  htmlID,
  isDisabled,
}: {
  label: string;
  value: string;
  onChange: (time: string) => void;
  required?: boolean;
  htmlID?: string;
  isDisabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle clicks outside both the input container and the picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Normalize legacy 'am'/'pm' values to full format
  const normalizeValue = (val: string): string => {
    if (!val) return '';
    if (val.toLowerCase() === 'am') return '12:01 AM';
    if (val.toLowerCase() === 'pm') return '12:01 PM';
    return val;
  };

  useEffect(() => {
    setInputValue(normalizeValue(value));
  }, [value]);

  // Parse "h:mm AM/PM" format to validate and normalize
  const parseTimeInput = (input: string): string | null => {
    const match = input.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i);
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();

    if (hours < 1 || hours > 12 || minutes > 59) return null;

    return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    const parsed = parseTimeInput(e.target.value);
    if (parsed) {
      onChange(parsed);
    }
  };

  const handleInputBlur = () => {
    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      setInputValue(parsed);
      onChange(parsed);
    } else if (value) {
      setInputValue(normalizeValue(value));
    }
  };

  // Convert "h:mm AM/PM" string to Date object for DatePicker
  const getDateFromTime = (time: string): Date | null => {
    const normalized = normalizeValue(time);
    if (!normalized) return null;

    const match = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();

    // Convert to 24h for Date object
    if (ampm === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0;
    }

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0);
    return date;
  };

  const handleTimePickerChange = (selectedDate: Date | null) => {
    if (selectedDate) {
      let hours = selectedDate.getHours();
      const minutes = selectedDate.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';

      hours = hours % 12;
      if (hours === 0) hours = 12;

      const timeStr = `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
      onChange(timeStr);
    }
    setIsOpen(false);
  };

  const handleClockClick = () => {
    if (!isDisabled) {
      if (!isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(true);
    }
  };

  return (
    <FormControl id={htmlID} isRequired={required} isDisabled={isDisabled}>
      <FormLabel htmlFor={htmlID} ml="0">
        {label}
      </FormLabel>
      <div
        ref={containerRef}
        className="custom-date-picker"
        style={{ position: 'relative' }}
      >
        <InputGroup>
          <Input
            ref={inputRef}
            variant="main"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onClick={handleInputClick}
            placeholder="12:01 AM"
            disabled={isDisabled}
            title={isDisabled ? 'Editing is disabled' : ''}
            borderColor={!inputValue && required ? 'red.600' : 'inherit'}
          />
          <InputRightElement width="auto" pr={2} zIndex={0}>
            <IconButton
              aria-label="Open time picker"
              icon={<CiClock1 size={20} />}
              size="sm"
              variant="ghost"
              onClick={handleClockClick}
              isDisabled={isDisabled}
            />
          </InputRightElement>
        </InputGroup>
        {isOpen && (
          <Portal>
            <div
              ref={pickerRef}
              className="custom-date-picker"
              style={{
                position: 'absolute',
                top: pickerPosition.top,
                left: pickerPosition.left,
                zIndex: 1400,
              }}
            >
              {/* @ts-ignore */}
              <DatePicker
                selected={getDateFromTime(value)}
                onChange={handleTimePickerChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                inline
                injectTimes={getInjectedTimes()}
                filterTime={filterTime}
              />
            </div>
          </Portal>
        )}
      </div>
    </FormControl>
  );
};

export default TimeField;
